import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Button, Paper, List, ListItem, ListItemText, ListItemButton,
  CircularProgress, Alert, Chip, IconButton, Tooltip, Divider, useMediaQuery, useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

const BusinessJobs = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/businesses/my-jobs');
      setJobs(res.data);
    } catch (err) {
      setError('Impossibile caricare le opportunità.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleDeleteJob = (jobId) => {
    if (window.confirm('Sei sicuro di voler cancellare questo annuncio? Verranno eliminate anche tutte le candidature associate.')) {
      axios.delete(`/api/jobs/${jobId}`)
        .then(() => {
          setJobs(jobs.filter(job => job.id !== jobId));
        })
        .catch(err => alert('Errore: impossibile cancellare l\'annuncio.'));
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 1 },
        mb: 3 
      }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Le Tue Opportunità di Lavoro
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => navigate('/jobs/new')}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
        >
          Pubblica Nuova
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {jobs.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Non hai ancora pubblicato nessuna opportunità di lavoro.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => navigate('/jobs/new')}
          >
            Pubblica Opportunità
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ overflowX: 'auto' }}>
          <List>
            {jobs.map((job, index) => (
              <React.Fragment key={job.id}>
                <ListItem
                  disablePadding
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    p: { xs: 2, sm: 2 },
                    gap: { xs: 1, sm: 0 }
                  }}
                  secondaryAction={
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 0, sm: 1 },
                      mt: { xs: 2, sm: 0 },
                      width: { xs: '100%', sm: 'auto' }
                    }}>
                      <Chip 
                        label={`${job._count?.applications || 0} Candidati`} 
                        color="primary" 
                        sx={{ display: { xs: 'none', sm: 'flex' } }}
                      />
                      <Tooltip title="Vedi Candidati">
                        <IconButton 
                          size="small"
                          onClick={() => navigate(`/jobs/${job.id}/applicants`)}
                        >
                          <RemoveRedEyeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Modifica">
                        <IconButton 
                          size="small"
                          onClick={() => navigate(`/jobs/edit/${job.id}`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancella">
                        <IconButton 
                          size="small"
                          edge="end" 
                          onClick={() => handleDeleteJob(job.id)}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemButton 
                    onClick={() => navigate(`/opportunita/${job.id}`)}
                    sx={{ flex: 1, p: 0 }}
                  >
                    <ListItemText 
                      primary={job.title}
                      secondary={
                        <Box>
                          <Typography variant="caption" component="div">
                            Pubblicato il {new Date(job.createdAt).toLocaleDateString('it-IT')}
                          </Typography>
                          {job.categories && job.categories.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              {job.categories.map(({ category }) => (
                                <Chip 
                                  key={category.id} 
                                  label={category.name} 
                                  size="small" 
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      }
                      primaryTypographyProps={{ sx: { fontWeight: 'bold' } }}
                    />
                  </ListItemButton>
                </ListItem>
                {index < jobs.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default BusinessJobs;
